export const DEFAULT_PUBLIC_CATALOG_JURISDICTION = 'ES'

export const PUBLIC_CATALOG_JURISDICTION_OPTIONS = [
	{ code: 'ES', label: 'España' },
	{ code: 'AR', label: 'Argentina' },
	{ code: 'CL', label: 'Chile' },
	{ code: 'CO', label: 'Colombia' },
	{ code: 'MX', label: 'México' },
	{ code: 'PE', label: 'Perú' },
	{ code: 'VE', label: 'Venezuela' },
] as const

export type PublicCatalogJurisdiction =
	(typeof PUBLIC_CATALOG_JURISDICTION_OPTIONS)[number]['code']

export type PublicCatalogJurisdictionOption = {
	code: string
	label: string
}

/**
 * Derives the country selector options from the jurisdiction codes that
 * currently have published content (PRD 039 Slice 5).
 *
 * Pure: the caller provides the published codes (repository read). The
 * default jurisdiction is always included because universal-scope content is
 * visible there without any CourseJurisdiction row. Codes outside the public
 * selector list (for example region codes such as LATAM) are ignored — the
 * selector only offers countries. Canonical list order is preserved, so the
 * selector appears automatically as soon as a second country publishes
 * content, with no code change.
 */
export function resolveAvailableCatalogJurisdictions(
	published: string[],
): PublicCatalogJurisdictionOption[] {
	const normalized = new Set(
		published.map((code) => code.trim().toUpperCase()).filter(Boolean),
	)
	return PUBLIC_CATALOG_JURISDICTION_OPTIONS.filter(
		(option) =>
			option.code === DEFAULT_PUBLIC_CATALOG_JURISDICTION ||
			normalized.has(option.code),
	).map((option) => ({ code: option.code, label: option.label }))
}

export function normalizePublicCatalogJurisdiction(
	value: string | null | undefined,
) {
	const normalized = value?.trim().toUpperCase()
	return normalized && /^[A-Z][A-Z0-9_-]{1,15}$/.test(normalized)
		? normalized
		: DEFAULT_PUBLIC_CATALOG_JURISDICTION
}

/**
 * Human label for a jurisdiction code, falling back to the code itself for
 * jurisdictions (e.g. regions) that are not part of the public selector list.
 */
export function resolvePublicCatalogJurisdictionLabel(
	code: string | null | undefined,
) {
	if (!code) return ''
	const normalized = code.trim().toUpperCase()
	const option = PUBLIC_CATALOG_JURISDICTION_OPTIONS.find(
		(candidate) => candidate.code === normalized,
	)
	return option?.label ?? normalized
}
